import { useState } from 'react';
import type { StorageLocation, StorageLocationType } from '@maker-toolchest/shared';
import { useStorageLocations, useDeleteStorageLocation } from '../hooks';
import { DataTable, type Column } from '@/components/DataTable';
import { SearchInput } from '@/components/SearchInput';
import { Select } from '@/components/Select';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FilterBar } from '@/components/FilterBar';
import { StorageLocationTypeBadge } from './StorageLocationTypeBadge';
import { Pencil, Trash2 } from 'lucide-react';

const typeFilterOptions = [
  { value: '', label: 'All Types' },
  { value: 'SHELF', label: 'Shelf' },
  { value: 'BIN', label: 'Bin' },
  { value: 'CABINET', label: 'Cabinet' },
  { value: 'DRYER', label: 'Dryer' },
  { value: 'OTHER', label: 'Other' },
];

export interface StorageLocationListProps {
  onEdit: (location: StorageLocation) => void;
}

export function StorageLocationList({ onEdit }: StorageLocationListProps) {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'type' | 'createdAt'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [deleteTarget, setDeleteTarget] = useState<StorageLocation | null>(null);
  const [searchKey, setSearchKey] = useState(0);

  const { data, isLoading } = useStorageLocations({
    page,
    limit: 20,
    search: search || undefined,
    type: (typeFilter || undefined) as StorageLocationType | undefined,
    sortBy,
    sortOrder,
  });

  const deleteMutation = useDeleteStorageLocation();

  const handleSort = (key: string) => {
    if (sortBy === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key as 'name' | 'type' | 'createdAt');
      setSortOrder('asc');
    }
    setPage(1);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      setDeleteTarget(null);
    } catch {
      // Error is handled by mutation
    }
  };

  const handleClearFilters = () => {
    setSearch('');
    setTypeFilter('');
    setPage(1);
  };

  const handleApplyFilters = (newFilters: Record<string, any>) => {
    // Set all filters at once
    setSearch(newFilters.search || '');
    setTypeFilter(newFilters.type || '');
    setPage(1);

    // Force remount of SearchInput to clear stale debounced state
    setSearchKey(prev => prev + 1);
  };

  const hasActiveFilters = !!(search || typeFilter);

  const columns: Column<StorageLocation>[] = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      render: (item) => (
        <span className="font-medium text-foreground">{item.name}</span>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      sortable: true,
      render: (item) => <StorageLocationTypeBadge type={item.type} />,
    },
    {
      key: 'description',
      header: 'Description',
      render: (item) => (
        <span className="text-foreground-muted truncate max-w-[200px] block">
          {item.description || '-'}
        </span>
      ),
    },
    {
      key: 'capacity',
      header: 'Capacity',
      align: 'center',
      render: (item) => (
        <span className="text-foreground-muted">
          {item.capacity ? `${item.capacity} spools` : 'Unlimited'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '100px',
      align: 'right',
      render: (item) => (
        <div className="flex items-center justify-end gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(item);
            }}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setDeleteTarget(item);
            }}
          >
            <Trash2 className="h-4 w-4 text-error" />
          </Button>
        </div>
      ),
    },
  ];

  const locations = data || [];

  return (
    <div className="space-y-4">
      {/* Filters */}
      <FilterBar
        searchField={
          <SearchInput
            key={searchKey}
            value={search}
            onChange={setSearch}
            placeholder="Search locations..."
          />
        }
        filterFields={[
          <div className="w-40">
            <Select
              label="Type"
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setPage(1);
              }}
              options={typeFilterOptions}
            />
          </div>,
        ]}
        filterConfigs={[
          { key: 'type', label: 'Type', type: 'select', options: typeFilterOptions },
        ]}
        filterValues={{
          search: search || '',
          type: typeFilter || '',
        }}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        hasActiveFilters={hasActiveFilters}
      />

      {/* Table */}
      <div className="rounded-lg border border-border overflow-hidden">
        <DataTable
          columns={columns}
          data={locations}
          keyExtractor={(item) => item.id}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          loading={isLoading}
          emptyMessage="No storage locations found"
        />

        {/* Pagination */}
        {/* TODO: Pagination support */}
        {/* {pagination && pagination.totalPages > 1 && (
          <Pagination
            page={pagination.page}
            totalPages={pagination.totalPages}
            total={pagination.total}
            limit={pagination.limit}
            onPageChange={setPage}
          />
        )} */}
      </div>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Storage Location"
        description={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
